import { rsosApi, baseQueryArgs, baseExtraOptions } from '../rsosApi';
import type {
  EventShare,
  EventShareApiResponse,
  CreateEventShareRequest,
  UpdateEventShareRequest,
  IarAgency,
  IarMember,
  IarMembersApiResponse,
} from './types';

/** Page size used when iterating through paginated IaR member responses. */
const IAR_MEMBERS_PAGE_SIZE = 50;

/**
 * Keep IaR roster data cached for 10 minutes after last subscription unmounts.
 * Agencies and their members change rarely (additions/removals are admin
 * actions, not real-time), and re-fetching the full paginated list each time
 * the dispatcher opens the IaR picker is wasteful.
 */
const IAR_ROSTER_CACHE_SECONDS = 10 * 60;

/**
 * Extends the rsosApi for event share endpoints.
 * Authorization header is included via baseQueryArgs to tell RTK which token to use.
 */
export const eventShareApi = rsosApi.injectEndpoints({
  overrideExisting: false,
  endpoints: builder => ({
    // GET - Fetch shares by phone number
    getEventSharesByPhone: builder.query<
      EventShare[],
      { org: string; phoneNumber: string }
    >({
      // `uri` is passed via `params` (not interpolated into the URL) so RTK
      // Query encodes it. A raw `tel://…` in the path contains `://`, which
      // trips fetchBaseQuery's isAbsoluteUrl check — it then treats the whole
      // relative path as absolute and skips baseUrl, so the request resolves
      // against the page origin (the portal host) instead of the API host.
      query: ({ org, phoneNumber }) => ({
        ...baseQueryArgs,
        url: `v1/event-share/${org}`,
        params: { uri: phoneNumber },
      }),
      extraOptions: { ...baseExtraOptions },
      providesTags: (result, error, { org, phoneNumber }) => [
        // Normalize phoneNumber to match invalidation tag (remove tel:// prefix)
        {
          type: 'EventShare',
          id: `${org}-phone-${phoneNumber.replace('tel://', '')}`,
        },
      ],
    }),

    // GET - Fetch shares by session ID
    getEventSharesBySession: builder.query<
      EventShareApiResponse,
      { org: string; sessionId: string }
    >({
      query: ({ org, sessionId }) => ({
        ...baseQueryArgs,
        url: `v1/event-share/${org}?session_id=${sessionId}`,
      }),
      extraOptions: { ...baseExtraOptions },
      providesTags: (result, error, { org, sessionId }) => [
        { type: 'EventShare', id: `${org}-session-${sessionId}` },
      ],
    }),

    // POST - Create initial share
    createEventShare: builder.mutation<
      EventShare,
      { org: string } & CreateEventShareRequest
    >({
      query: ({ org, ...body }) => ({
        ...baseQueryArgs,
        url: `v1/event-share/${org}`,
        method: 'POST',
        body,
      }),
      extraOptions: { ...baseExtraOptions },
      invalidatesTags: (result, error, { org }) => {
        const tags: { type: 'EventShare'; id: string }[] = [];
        if (result?.uri) {
          tags.push({
            type: 'EventShare',
            id: `${org}-phone-${result.uri.replace('tel://', '')}`,
          });
        }
        if (result?.session_id) {
          tags.push({
            type: 'EventShare',
            id: `${org}-session-${result.session_id}`,
          });
        }
        return tags;
      },
    }),

    // PATCH - Update existing share using session ID
    updateEventShare: builder.mutation<
      EventShare,
      {
        org: string;
        sessionId: string;
        uri?: string;
      } & UpdateEventShareRequest
    >({
      query: ({ org, sessionId, ...body }) => ({
        ...baseQueryArgs,
        url: `v1/event-share/${org}/${sessionId}`,
        method: 'PATCH',
        body,
      }),
      extraOptions: { ...baseExtraOptions },
      invalidatesTags: (_result, _error, { org, sessionId, uri }) => {
        const tags: { type: 'EventShare'; id: string }[] = [
          { type: 'EventShare', id: `${org}-session-${sessionId}` },
        ];
        // Also invalidate phone-based query if uri is provided
        if (uri) {
          tags.push({
            type: 'EventShare',
            id: `${org}-phone-${uri.replace('tel://', '')}`,
          });
        }
        return tags;
      },
    }),

    // DELETE - Remove specific recipient by share_key
    deleteRecipient: builder.mutation<
      void,
      { org: string; sessionId: string; shareKey: string; uri?: string }
    >({
      query: ({ org, sessionId, shareKey }) => ({
        ...baseQueryArgs,
        url: `v1/event-share/${org}/${sessionId}/recipients/${shareKey}`,
        method: 'DELETE',
      }),
      extraOptions: { ...baseExtraOptions },
      invalidatesTags: (_result, _error, { org, sessionId, uri }) => {
        const tags: { type: 'EventShare'; id: string }[] = [
          { type: 'EventShare', id: `${org}-session-${sessionId}` },
        ];
        // Also invalidate phone-based query if uri is provided
        if (uri) {
          tags.push({
            type: 'EventShare',
            id: `${org}-phone-${uri.replace('tel://', '')}`,
          });
        }
        return tags;
      },
    }),

    // GET - Fetch IaR agencies linked to the org
    getIarAgencies: builder.query<IarAgency[], { org: string }>({
      query: ({ org }) => ({
        ...baseQueryArgs,
        url: `v1/event-share/iar/${org}/agencies`,
      }),
      extraOptions: { ...baseExtraOptions },
      keepUnusedDataFor: IAR_ROSTER_CACHE_SECONDS,
    }),

    // GET - Fetch all members of a specific IaR agency (fetches all pages)
    getIarAgencyMembers: builder.query<
      IarMember[],
      { org: string; agencyId: number }
    >({
      async queryFn({ org, agencyId }, _queryApi, _extraOptions, fetchWithBQ) {
        const buildUrl = (page: number) =>
          `v1/event-share/iar/${org}/agencies/${agencyId}/members?page=${page}&pageSize=${IAR_MEMBERS_PAGE_SIZE}`;

        // Fetch the first page sequentially to learn the total page count.
        const firstResult = await fetchWithBQ({
          ...baseQueryArgs,
          url: buildUrl(1),
        });

        if (firstResult.error) {
          return { error: firstResult.error };
        }

        const firstPage = firstResult.data as IarMembersApiResponse;
        if (firstPage.pageCount <= 1) {
          return { data: firstPage.results };
        }

        // Fetch the remaining pages in parallel — the IaR proxy caps pageSize
        // at 50, so large agencies need many pages and a sequential walk gets
        // slow (e.g. 13 round-trips of ~200ms each).
        const remainingPages = Array.from(
          { length: firstPage.pageCount - 1 },
          (_, i) => i + 2,
        );
        const remainingResults = await Promise.all(
          remainingPages.map(page =>
            fetchWithBQ({ ...baseQueryArgs, url: buildUrl(page) }),
          ),
        );

        const errored = remainingResults.find(r => r.error);
        if (errored?.error) {
          return { error: errored.error };
        }

        return {
          data: [
            ...firstPage.results,
            ...remainingResults.flatMap(
              r => (r.data as IarMembersApiResponse).results,
            ),
          ],
        };
      },
      extraOptions: { ...baseExtraOptions },
      keepUnusedDataFor: IAR_ROSTER_CACHE_SECONDS,
    }),

    // DELETE - Delete a share session
    deleteEventShare: builder.mutation<
      void,
      { org: string; sessionId: string; uri?: string }
    >({
      query: ({ org, sessionId }) => ({
        ...baseQueryArgs,
        url: `v1/event-share/${org}/${sessionId}`,
        method: 'DELETE',
      }),
      extraOptions: { ...baseExtraOptions },
      invalidatesTags: (_result, _error, { org, sessionId, uri }) => {
        const tags: { type: 'EventShare'; id: string }[] = [
          { type: 'EventShare', id: `${org}-session-${sessionId}` },
        ];
        // Also invalidate phone-based query if uri is provided
        if (uri) {
          tags.push({
            type: 'EventShare',
            id: `${org}-phone-${uri.replace('tel://', '')}`,
          });
        }
        return tags;
      },
    }),
  }),
});

export const {
  useGetEventSharesByPhoneQuery,
  useGetEventSharesBySessionQuery,
  useCreateEventShareMutation,
  useUpdateEventShareMutation,
  useDeleteRecipientMutation,
  useDeleteEventShareMutation,
  useGetIarAgenciesQuery,
  useGetIarAgencyMembersQuery,
} = eventShareApi;
